import { isBefore, subDays } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type EngagementLevel = "poor" | "average" | "good";

const EngagementLevelMap: Record<EngagementLevel, string> = {
    poor: "Poor",
    average: "Average",
    good: "Good",
};

function calculateEngagementFromDate(
    now: Date,
    lastMessageAt: Date | null | undefined,
): { level: EngagementLevel; label: string } {
    if (!lastMessageAt) {
        return { level: "poor", label: EngagementLevelMap.poor };
    }

    const good = subDays(now, 7);
    const average = subDays(now, 14);

    if (isBefore(lastMessageAt, average)) {
        return { level: "poor", label: EngagementLevelMap.poor };
    }

    if (isBefore(lastMessageAt, good)) {
        return { level: "average", label: EngagementLevelMap.average };
    }

    return { level: "good", label: EngagementLevelMap.good };
}

export function engagementTooltip(lastMessageAt: Date | null | undefined): string {
    if (!lastMessageAt) {
        return "Haven't messaged with us";
    }

    const engagement = calculateEngagementFromDate(new Date(), lastMessageAt);
    switch (engagement.level) {
        case "poor":
            return "Messaged us over 2 weeks ago";
        case "average":
            return "Messaged us 1-2 weeks ago";
        case "good":
            return "Messaged us within a week";
        default: {
            const _exhaustive: never = engagement.level;
            return _exhaustive;
        }
    }
}

function EngagementTag({
    className,
    engagement,
}: {
    className?: string;
    engagement: { level: EngagementLevel; label: string };
}) {
    switch (engagement.level) {
        case "good":
            return (
                <Badge variant="green" size="sm" className={className}>
                    {engagement.label}
                </Badge>
            );
        case "average":
            return (
                <Badge variant="amber" size="sm" className={className}>
                    {engagement.label}
                </Badge>
            );
        case "poor":
            return (
                <Badge variant="red" size="sm" className={className}>
                    {engagement.label}
                </Badge>
            );
        default: {
            const _exhaustive: never = engagement.level;
            return _exhaustive;
        }
    }
}

export function EngagementIndicator({
    lastMessageAt,
    className,
}: {
    lastMessageAt: Date | null | undefined;
    className?: string;
}) {
    const engagement = calculateEngagementFromDate(new Date(), lastMessageAt);

    return (
        <Tooltip>
            <TooltipTrigger render={<span className="inline-flex" />}>
                <EngagementTag className={className} engagement={engagement} />
            </TooltipTrigger>
            <TooltipContent side="top" align="start">
                {engagementTooltip(lastMessageAt)}
            </TooltipContent>
        </Tooltip>
    );
}
